import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import { FloorPlanState } from '../types/stateProviders/FloorPlanState';
import { useQuoteDispatch, useQuoteState } from './QuoteStateProvider';

const FloorPlanStateContext = createContext<any | null>(null);
const FloorPlanDispatchContext = createContext<
	(newState: Partial<FloorPlanState>) => void
>(() => {});

export const FloorPlanStateProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const quoteState = useQuoteState();
	const quoteDispatch = useQuoteDispatch();

	const initialFloorPlanState: FloorPlanState = {
		drawingShape: false,
		corners: [],
		blocks: [],
		wallLengthsInInches: [],
		wallTypes: [],
		pixelToInchRatio: 0,
		drawspaceBoundingRect: new DOMRect(),
		showJoists: false,
		zoomLevel: 1,
		panOffset: {
			x: 0,
			y: 0,
		},
		materials: {},
		deckboxHeightInFeet: 0,
	};

	const [floorPlanState, setFloorPlanState] = useState<FloorPlanState>(
		initialFloorPlanState
	);
	const updateFloorPlanState = useCallback(
		(newFloorPlanState: Partial<FloorPlanState>) => {
			setFloorPlanState((prevState: FloorPlanState) => {
				return { ...prevState, ...newFloorPlanState };
			});
		},
		[]
	);

	useEffect(() => {
		updateFloorPlanState({
			corners: quoteState.corners,
			blocks: quoteState.blocks,
			pixelToInchRatio: quoteState.pixelToInchRatio || 0,
			panOffset: quoteState.panOffset || { x: 0, y: 0 },
			zoomLevel: quoteState.zoomLevel || 1,
			deckboxHeightInFeet: quoteState.deckboxHeightInFeet || 0,
		});
	}, [
		quoteState.corners,
		quoteState.blocks,
		quoteState.pixelToInchRatio,
		quoteState.panOffset,
		quoteState.zoomLevel,
	]);

	//push drawing errors up to quote state for validation
	useEffect(() => {
		const errors = [];

		if (floorPlanState.blocks.length === 0) {
			errors.push('No blocks drawn');
		}

		quoteDispatch({ drawingErrors: errors });
	}, [floorPlanState.blocks]);

	return (
		<FloorPlanStateContext.Provider value={floorPlanState}>
			<FloorPlanDispatchContext.Provider value={updateFloorPlanState}>
				{children}
			</FloorPlanDispatchContext.Provider>
		</FloorPlanStateContext.Provider>
	);
};

export function useFloorPlanState(): FloorPlanState {
	return useContext(FloorPlanStateContext);
}

export function useFloorPlanDispatch(): React.Dispatch<
	Partial<FloorPlanState>
> {
	return useContext(FloorPlanDispatchContext);
}
